import { supabase } from '@/integrations/supabase/client';

export interface AppIdea {
  id: string;
  user_id: string | null;
  original_problem: string;
  category: string | null;
  frequency: string | null;
  current_solution: string | null;
  priority: string | null;
  sharing: string | null;
  platform: string | null;
  additional_details: string | null;
  generated_title: string | null;
  generated_description: string | null;
  research_overview: string | null;
  research_requirements: string | null;
  research_enhanced_features: string | null;
  research_citations: string[] | null;
  research_related_questions: any[] | null;

  processing_status: string;
  title_generation_status: string;
  description_generation_status: string;
  research_generation_status: string;
  title_generation_error: string | null;
  description_generation_error: string | null;
  research_generation_error: string | null;
  vote_count: number;
  view_count: number;
  status: string;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  // Enhanced fields for UI
  is_favorited?: boolean;
  favorite_count?: number;
  // Transformed fields to match AppCard interface
  name?: string; // Will be generated_title
  description?: string; // Will be generated_description
  route?: string; // Will be placeholder
  icon_name?: string; // Will be based on category
}

export interface AppIdeaFavorite {
  id: string;
  user_id: string;
  app_idea_id: string;
  created_at: string;
}

/**
 * Delete an app idea from the database
 */
export async function deleteAppIdea(appIdeaId: string) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  // First check if the user owns this app idea or is admin
  const { data: appIdea, error: fetchError } = await supabase
    .from('app_ideas')
    .select('user_id')
    .eq('id', appIdeaId)
    .single();

  if (fetchError) throw new Error(`Failed to fetch app idea: ${fetchError.message}`);
  
  const isOwner = appIdea.user_id === user.id;
  const isAdmin = user.email?.includes('@admin');
  
  if (!isOwner && !isAdmin) {
    throw new Error('You do not have permission to delete this app idea');
  }

  // Delete the app idea
  const { error } = await supabase
    .from('app_ideas')
    .delete()
    .eq('id', appIdeaId);

  if (error) throw new Error(`Failed to delete app idea: ${error.message}`);
  
  return { appIdeaId, userId: user.id };
}

/**
 * Fetch all completed app ideas from the database with favorite counts
 * Only shows ideas that have completed processing (all AI generation done)
 */
export async function fetchAppIdeas() {
  const { data, error } = await supabase
    .from('app_ideas')
    .select('*')
    .eq('processing_status', 'completed')
    .order('vote_count', { ascending: false })
    .order('created_at', { ascending: false });
  
  if (error) throw error;
  
  console.log('[fetchAppIdeas] Raw database data:', {
    count: data?.length || 0,
    firstItem: data?.[0],
    firstItemCitations: data?.[0]?.research_citations,
    firstItemRelatedQuestions: data?.[0]?.research_related_questions,

  });

  // Helper function to normalize related questions (same as Research Lightbox)
  const normalizeRelatedQuestions = (questions: any): any[] => {
    if (!questions) return [];
    if (Array.isArray(questions)) return questions;
    // Handle case where it might be stored as jsonb but needs parsing
    try {
      const parsed = JSON.parse(JSON.stringify(questions));
      return Array.isArray(parsed) ? parsed : [];
    } catch (parseError) {
      console.warn('[fetchAppIdeas] Failed to parse research_related_questions, treating as empty array');
      return [];
    }
  };
  
  // Transform the data to match AppCard interface
  const transformedData = data?.map(item => {
    // Map category to appropriate icon
    const categoryIconMap: Record<string, string> = {
      'productivity': 'LayoutGrid',
      'creative': 'PenTool',
      'education': 'SquareLibrary',
      'finance': 'DollarSign',
      'health': 'Heart',
      'entertainment': 'Gamepad2',
      'shopping': 'ShoppingBag'
    };

    const transformed = {
      ...item,
      // Transform to match AppCard interface
      name: item.generated_title || 'Untitled App Idea',
      display_name: item.generated_title || 'Untitled App Idea',
      description: item.generated_description || item.original_problem,
      route: `/app-ideas/${item.id}`, // Placeholder route
      icon_name: categoryIconMap[item.category || ''] || 'Lightbulb',
      // Use the favorite_count column directly
      favorite_count: item.favorite_count || 0,
      // Set status to 'available' so app ideas appear fully visible (not greyed out)
      status: 'available',
      // Keep priority as string to match AppItem interface
      priority: item.priority || null,
      // Normalize related questions to ensure proper data types (same as Research Lightbox)
      research_related_questions: normalizeRelatedQuestions(item.research_related_questions),

    };

    console.log('[fetchAppIdeas] Transformed item:', {
      id: transformed.id,
      name: transformed.name,
      hasCitations: !!transformed.research_citations,
      citationsCount: transformed.research_citations?.length || 0,
      citations: transformed.research_citations,
      hasRelatedQuestions: !!transformed.research_related_questions,
      relatedQuestionsCount: transformed.research_related_questions?.length || 0,

    });

    return transformed;
  }) || [];
  
  return transformedData as AppIdea[];
}

/**
 * Fetch user favorites for app ideas for the current user
 */
export async function fetchAppIdeaFavorites() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return []; // Return empty array if no user
  }

  const { data, error } = await supabase
    .from('app_idea_favorites')
    .select('*')
    .eq('user_id', user.id);
  
  if (error) {
    console.error('Error fetching app idea favorites:', error);
    throw error;
  }
  return data as AppIdeaFavorite[];
}

/**
 * Toggle favorite status for an app idea
 */
export async function toggleAppIdeaFavorite(appIdeaId: string, isFavorited: boolean) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  if (isFavorited) {
    // Remove favorite
    const { error } = await supabase
      .from('app_idea_favorites')
      .delete()
      .eq('app_idea_id', appIdeaId)
      .eq('user_id', user.id);
    
    if (error) throw error;
  } else {
    // Add favorite
    const { error } = await supabase
      .from('app_idea_favorites')
      .insert({ 
        app_idea_id: appIdeaId,
        user_id: user.id
      });
    
    if (error) throw error;
  }

  return { appIdeaId, isFavorited, userId: user.id };
}
